"use client";

import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Search, Star } from "lucide-react";
import { Layout9 } from "@/components/layouts/layout-9";
import { ScreenLoader } from "@/components/screen-loader";
import {
  Toolbar,
  ToolbarHeading,
} from "@/components/layouts/layout-9/components/toolbar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTable } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const stocks = [
  {
    symbol: "THYAO",
    name: "Türk Hava Yolları",
    market: "BIST 30",
    price: "318,75",
    change: 2.48,
    volume: "4,82 Mr ₺",
    marketCap: "439,7 Mr ₺",
    color: "bg-red-600",
    sparkline: "2,27 18,24 33,25 48,17 63,20 78,10 94,14 110,5",
  },
  {
    symbol: "ASELS",
    name: "Aselsan",
    market: "BIST 30",
    price: "93,40",
    change: 1.85,
    volume: "3,16 Mr ₺",
    marketCap: "425,9 Mr ₺",
    color: "bg-sky-700",
    sparkline: "2,29 18,26 33,28 48,19 63,22 78,15 94,17 110,8",
  },
  {
    symbol: "GARAN",
    name: "Garanti BBVA",
    market: "BIST 30",
    price: "134,20",
    change: 0.72,
    volume: "2,74 Mr ₺",
    marketCap: "563,6 Mr ₺",
    color: "bg-emerald-600",
    sparkline: "2,24 18,19 33,22 48,14 63,17 78,12 94,15 110,9",
  },
  {
    symbol: "TUPRS",
    name: "Tüpraş",
    market: "BIST 30",
    price: "172,60",
    change: -1.24,
    volume: "2,19 Mr ₺",
    marketCap: "332,6 Mr ₺",
    color: "bg-zinc-800",
    sparkline: "2,8 18,11 33,9 48,16 63,14 78,21 94,19 110,28",
  },
  {
    symbol: "EREGL",
    name: "Ereğli Demir Çelik",
    market: "BIST 30",
    price: "27,84",
    change: -0.64,
    volume: "1,87 Mr ₺",
    marketCap: "194,9 Mr ₺",
    color: "bg-blue-700",
    sparkline: "2,10 18,14 33,12 48,18 63,16 78,24 94,21 110,25",
  },
  {
    symbol: "KCHOL",
    name: "Koç Holding",
    market: "BIST 30",
    price: "187,10",
    change: 1.12,
    volume: "1,42 Mr ₺",
    marketCap: "474,4 Mr ₺",
    color: "bg-rose-700",
    sparkline: "2,26 18,23 33,25 48,18 63,20 78,13 94,16 110,10",
  },
  {
    symbol: "SISE",
    name: "Şişecam",
    market: "BIST 50",
    price: "39,16",
    change: -1.85,
    volume: "1,08 Mr ₺",
    marketCap: "119,9 Mr ₺",
    color: "bg-cyan-700",
    sparkline: "2,7 18,12 33,10 48,17 63,15 78,23 94,20 110,29",
  },
  {
    symbol: "BIMAS",
    name: "BİM Mağazalar",
    market: "BIST 30",
    price: "548,50",
    change: 0.36,
    volume: "912,4 Mn ₺",
    marketCap: "332,9 Mr ₺",
    color: "bg-red-700",
    sparkline: "2,21 18,17 33,20 48,16 63,18 78,13 94,15 110,12",
  },
];

export default function HomePage() {
  const [isLoading, setIsLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [activeMarket, setActiveMarket] = useState("Tümü");
  const [favorites, setFavorites] = useState<string[]>(["THYAO", "ASELS"]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 400);
    return () => clearTimeout(timer);
  }, []);

  if (isLoading) {
    return <ScreenLoader />;
  }

  const visibleStocks = stocks.filter((stock) => {
    const matchesMarket =
      activeMarket === "Tümü" || stock.market === activeMarket;
    const searchValue = query.trim().toLocaleLowerCase("tr-TR");
    const matchesSearch =
      stock.symbol.toLocaleLowerCase("tr-TR").includes(searchValue) ||
      stock.name.toLocaleLowerCase("tr-TR").includes(searchValue);

    return matchesMarket && matchesSearch;
  });

  const toggleFavorite = (symbol: string) => {
    setFavorites((current) =>
      current.includes(symbol)
        ? current.filter((item) => item !== symbol)
        : [...current, symbol],
    );
  };

  return (
    <Layout9>
      <Toolbar>
        <ToolbarHeading title="Piyasa Takibi" />
      </Toolbar>
      <div className="container pb-8">
        <Card className="overflow-hidden">
          <CardHeader className="min-h-auto flex-col items-stretch gap-4 border-b px-4 py-4 sm:flex-row sm:items-center sm:px-5">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex size-9 shrink-0 items-center justify-center rounded-lg border bg-muted/50">
                <span className="text-xs font-bold tracking-tight">BİST</span>
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h2 className="truncate text-sm font-semibold text-foreground">
                    Hisse Senetleri
                  </h2>
                  <Badge
                    variant="success"
                    appearance="light"
                    size="sm"
                    className="gap-1.5"
                  >
                    <span className="size-1.5 rounded-full bg-current" />
                    Açık
                  </Badge>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Borsa İstanbul · Veriler 15 dk gecikmelidir
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="relative sm:w-56">
                <Search className="absolute start-3 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Sembol veya şirket ara"
                  aria-label="Hisse senedi ara"
                  className="ps-9"
                />
              </div>
              <div className="flex items-center rounded-md border bg-muted/40 p-0.5">
                {["Tümü", "BIST 30", "BIST 50"].map((market) => (
                  <button
                    key={market}
                    type="button"
                    onClick={() => setActiveMarket(market)}
                    className={`h-7 flex-1 rounded-sm px-2.5 text-xs font-medium transition-colors sm:flex-none ${
                      activeMarket === market
                        ? "bg-background text-foreground shadow-xs"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {market}
                  </button>
                ))}
              </div>
            </div>
          </CardHeader>

          <CardTable>
            <Table className="min-w-[820px]">
              <TableHeader>
                <TableRow className="bg-muted/35 hover:bg-muted/35">
                  <TableHead className="w-12 px-4" aria-label="Favoriler" />
                  <TableHead className="min-w-56 px-2">Hisse</TableHead>
                  <TableHead className="text-end">Son fiyat</TableHead>
                  <TableHead className="text-end">Günlük</TableHead>
                  <TableHead className="w-36 text-center">Gün içi</TableHead>
                  <TableHead className="text-end">Hacim</TableHead>
                  <TableHead className="pe-5 text-end">Piyasa değeri</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleStocks.map((stock) => {
                  const isPositive = stock.change >= 0;
                  const isFavorite = favorites.includes(stock.symbol);

                  return (
                    <TableRow key={stock.symbol} className="group h-[68px]">
                      <TableCell className="px-4 py-3">
                        <Button
                          type="button"
                          variant="ghost"
                          mode="icon"
                          size="sm"
                          aria-label={`${stock.symbol} ${isFavorite ? "favorilerden çıkar" : "favorilere ekle"}`}
                          onClick={() => toggleFavorite(stock.symbol)}
                          className={
                            isFavorite
                              ? "text-amber-500 hover:text-amber-600"
                              : "text-muted-foreground/50 hover:text-amber-500"
                          }
                        >
                          <Star className={isFavorite ? "fill-current" : ""} />
                        </Button>
                      </TableCell>
                      <TableCell className="px-2 py-3">
                        <div className="flex items-center gap-3">
                          <div
                            className={`flex size-9 shrink-0 items-center justify-center rounded-lg text-[11px] font-semibold text-white shadow-xs ${stock.color}`}
                          >
                            {stock.symbol.slice(0, 2)}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold tracking-tight text-foreground">
                                {stock.symbol}
                              </span>
                              <span className="rounded border px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                                {stock.market}
                              </span>
                            </div>
                            <p className="mt-1 max-w-40 truncate text-xs text-muted-foreground">
                              {stock.name}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-end font-semibold tabular-nums">
                        {stock.price} ₺
                      </TableCell>
                      <TableCell className="text-end">
                        <span
                          className={`inline-flex items-center justify-end gap-1 font-medium tabular-nums ${
                            isPositive ? "text-emerald-600" : "text-red-600"
                          }`}
                        >
                          {isPositive ? (
                            <ArrowUp className="size-3.5" />
                          ) : (
                            <ArrowDown className="size-3.5" />
                          )}
                          %{Math.abs(stock.change).toFixed(2).replace(".", ",")}
                        </span>
                      </TableCell>
                      <TableCell className="py-3 text-center">
                        <svg
                          viewBox="0 0 112 36"
                          className={`mx-auto h-9 w-28 ${
                            isPositive ? "text-emerald-500" : "text-red-500"
                          }`}
                          role="img"
                          aria-label={`${stock.symbol} gün içi fiyat eğrisi`}
                        >
                          <line
                            x1="0"
                            y1="35"
                            x2="112"
                            y2="35"
                            stroke="currentColor"
                            strokeOpacity="0.12"
                          />
                          <polyline
                            points={stock.sparkline}
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </TableCell>
                      <TableCell className="text-end text-sm tabular-nums text-secondary-foreground">
                        {stock.volume}
                      </TableCell>
                      <TableCell className="pe-5 text-end text-sm tabular-nums text-secondary-foreground">
                        {stock.marketCap}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {visibleStocks.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="h-32 text-center">
                      <p className="font-medium">Sonuç bulunamadı</p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Arama ifadenizi veya piyasa filtresini değiştirin.
                      </p>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardTable>
        </Card>
      </div>
    </Layout9>
  );
}
